import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Line } from 'react-chartjs-2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine, faCode, faQuestionCircle, faLightbulb, faStar } from '@fortawesome/free-solid-svg-icons';

const MarkupCalculatorClassic = () => {
  const [costPrice, setCostPrice] = useState('');
  const [markupPercentage, setMarkupPercentage] = useState('');
  const [sellingPrice, setSellingPrice] = useState('');
  const [profit, setProfit] = useState('');
  const [profitPercentage, setProfitPercentage] = useState('');
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const calculateMarkup = () => {
    try {
      const cp = parseFloat(costPrice);
      const markup = parseFloat(markupPercentage);

      if (isNaN(cp) || cp <= 0) {
        throw new Error('Cost price must be a positive number');
      }

      if (isNaN(markup) || markup < 0) {
        throw new Error('Markup percentage must be a positive number');
      }

      const sp = cp * (1 + markup / 100);
      const profitAmount = sp - cp;
      const profitPercent = (profitAmount / cp) * 100;

      setSellingPrice(sp.toFixed(2));
      setProfit(profitAmount.toFixed(2));
      setProfitPercentage(profitPercent.toFixed(2));
      setError('');

      // Update chart data
      setChartData({
        labels: ['Cost Price', 'Selling Price'],
        datasets: [{
          label: 'Price Comparison',
          data: [cp, sp],
          borderColor: 'rgb(236, 72, 153)',
          tension: 0.1,
        }],
      });
    } catch (err) {
      setError(err.message);
      setSellingPrice('');
      setProfit('');
      setProfitPercentage('');
    }
  };

  const calculateFromSellingPrice = () => {
    try {
      const cp = parseFloat(costPrice);
      const sp = parseFloat(sellingPrice);

      if (isNaN(cp) || cp <= 0) {
        throw new Error('Cost price must be a positive number');
      }

      if (isNaN(sp) || sp <= cp) {
        throw new Error('Selling price must be greater than cost price');
      }

      const markup = ((sp - cp) / cp) * 100;
      const profitAmount = sp - cp;
      const profitPercent = (profitAmount / cp) * 100;

      setMarkupPercentage(markup.toFixed(2));
      setProfit(profitAmount.toFixed(2));
      setProfitPercentage(profitPercent.toFixed(2));
      setError('');

      // Update chart data
      setChartData({
        labels: ['Cost Price', 'Selling Price'],
        datasets: [{
          label: 'Price Comparison',
          data: [cp, sp],
          borderColor: 'rgb(236, 72, 153)',
          tension: 0.1,
        }],
      });
    } catch (err) {
      setError(err.message);
      setMarkupPercentage('');
      setProfit('');
      setProfitPercentage('');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 to-blue-100">
      <Helmet>
        <title>Markup Calculator Classic</title>
        <meta name="description" content="Calculate markup, selling price, and profit percentage with ease. Perfect for business owners and sales professionals." />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <FontAwesomeIcon 
            icon={faChartLine} 
            className="text-4xl text-blue-600 mb-4" 
          />
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Markup Calculator Classic</h1>
          <p className="text-xl text-gray-600">Calculate your markup, selling price, and profit percentage</p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h2 className="text-xl font-semibold mb-4 text-blue-600">Calculate Markup</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Cost Price</label>
                  <input
                    type="number"
                    value={costPrice}
                    onChange={(e) => setCostPrice(e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Markup Percentage</label>
                  <input
                    type="number"
                    value={markupPercentage}
                    onChange={(e) => setMarkupPercentage(e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <button
                    onClick={calculateMarkup}
                    className="w-full bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    Calculate Selling Price
                  </button>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-xl font-semibold mb-4 text-blue-600">Calculate from Selling Price</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Cost Price</label>
                  <input
                    type="number"
                    value={costPrice}
                    onChange={(e) => setCostPrice(e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Selling Price</label>
                  <input
                    type="number"
                    value={sellingPrice}
                    onChange={(e) => setSellingPrice(e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <button
                    onClick={calculateFromSellingPrice}
                    className="w-full bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    Calculate Markup
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8">
            <h2 className="text-xl font-semibold mb-4 text-blue-600">Results</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold mb-2 text-gray-700">Selling Price</h3>
                <p className="text-2xl font-bold text-blue-600">${sellingPrice || '0.00'}</p>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold mb-2 text-gray-700">Markup Percentage</h3>
                <p className="text-2xl font-bold text-blue-600">{markupPercentage || '0.00'}%</p>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold mb-2 text-gray-700">Profit Amount</h3>
                <p className="text-2xl font-bold text-blue-600">${profit || '0.00'}</p>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold mb-2 text-gray-700">Profit Percentage</h3>
                <p className="text-2xl font-bold text-blue-600">{profitPercentage || '0.00'}%</p>
              </div>
            </div>
          </div>

          {chartData && (
            <div className="mt-8">
              <h2 className="text-xl font-semibold mb-4 text-blue-600">Price Comparison Graph</h2>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="bg-gray-50 p-4 rounded-lg mb-4">
                  <p className="text-sm text-green-600">Cost Price: ${chartData.datasets[0].data[0].toFixed(2)}</p>
                  <p className="text-sm text-green-600">Selling Price: ${chartData.datasets[0].data[1].toFixed(2)}</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div className="bg-gray-100 p-4 rounded-lg">
                    <div className="flex items-center mb-2">
                      <FontAwesomeIcon icon={faChartLine} className="text-blue-600 mr-3" />
                      <h3 className="text-lg font-semibold text-gray-700">Graph Overview</h3>
                    </div>
                    <ul className="list-disc pl-4 space-y-2 text-sm text-gray-600">
                      <li>The line shows the progression from cost price to selling price</li>
                      <li>The difference between these points represents your profit margin</li>
                      <li>The steeper the line, the higher your markup percentage</li>
                    </ul>
                  </div>
                  <div className="bg-gray-100 p-4 rounded-lg">
                    <div className="flex items-center mb-2">
                      <FontAwesomeIcon icon={faQuestionCircle} className="text-green-500 mr-3" />
                      <h3 className="text-lg font-semibold text-gray-700">Reading the Graph</h3>
                    </div>
                    <ul className="list-disc pl-4 space-y-2 text-sm text-gray-600">
                      <li>Left point: Your cost price (starting point)</li>
                      <li>Right point: Your selling price (final price)</li>
                      <li>Vertical distance: Your profit amount</li>
                      <li>Line slope: Shows markup percentage</li>
                    </ul>
                  </div>
                </div>
                <div className="relative h-[400px]">
                  <Line
                    data={chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: 'top',
                          labels: {
                            font: {
                              size: 14
                            }
                          }
                        },
                        title: {
                          display: true,
                          text: 'Cost vs Selling Price Comparison',
                          font: {
                            size: 16
                          }
                        },
                        tooltip: {
                          callbacks: {
                            label: function(context) {
                              const label = context.dataset.label || '';
                              const value = context.parsed.y;
                              return `${label}: $${value.toFixed(2)}`;
                            }
                          }
                        }
                      },
                      scales: {
                        x: {
                          grid: {
                            color: 'rgba(170, 170, 170, 0.2)'
                          },
                          ticks: {
                            font: {
                              size: 12
                            }
                          }
                        },
                        y: {
                          beginAtZero: true,
                          grid: {
                            color: 'rgba(170, 170, 170, 0.2)'
                          },
                          ticks: {
                            font: {
                              size: 12
                            },
                            callback: function(value) {
                              return '$' + value.toFixed(0);
                            }
                          }
                        }
                      },
                      animation: {
                        duration: 1000,
                        easing: 'easeInOutQuad'
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="mt-4 p-4 bg-red-100 text-red-700 rounded-md">
              {error}
            </div>
          )}
        </div>

        <div className="mt-12">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4 text-blue-600">How to Use</h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-gray-700">Calculate Markup:</h3>
                <ol className="list-decimal pl-6 mt-2 space-y-2">
                  <li>Enter your cost price</li>
                  <li>Enter your desired markup percentage</li>
                  <li>Click "Calculate Selling Price"</li>
                  <li>View the calculated selling price, profit amount, and profit percentage</li>
                </ol>
              </div>
              <div>
                <h3 className="font-semibold text-gray-700">Calculate from Selling Price:</h3>
                <ol className="list-decimal pl-6 mt-2 space-y-2">
                  <li>Enter your cost price</li>
                  <li>Enter your selling price</li>
                  <li>Click "Calculate Markup"</li>
                  <li>View the calculated markup percentage, profit amount, and profit percentage</li>
                </ol>
              </div>
            </div>
          </div>

          <div className="mt-8 bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4 text-blue-600">Features & Benefits</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="flex items-center mb-3">
                  <FontAwesomeIcon icon={faLightbulb} className="text-yellow-500 mr-3" />
                  <h3 className="font-semibold text-gray-700">Dual Calculation Modes</h3>
                </div>
                <p className="text-gray-600">Calculate markup from cost price and percentage, or calculate markup percentage from cost and selling prices.</p>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="flex items-center mb-3">
                  <FontAwesomeIcon icon={faChartLine} className="text-blue-600 mr-3" />
                  <h3 className="font-semibold text-gray-700">Visual Price Comparison</h3>
                </div>
                <p className="text-gray-600">View a clear line graph showing the relationship between cost price and selling price.</p>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="flex items-center mb-3">
                  <FontAwesomeIcon icon={faQuestionCircle} className="text-green-500 mr-3" />
                  <h3 className="font-semibold text-gray-700">Profit Analysis</h3>
                </div>
                <p className="text-gray-600">Calculate both profit amount and profit percentage for better business insights.</p>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="flex items-center mb-3">
                  <FontAwesomeIcon icon={faCode} className="text-purple-500 mr-3" />
                  <h3 className="font-semibold text-gray-700">Real-time Calculations</h3>
                </div>
                <p className="text-gray-600">Get instant results as you input your values without any delay.</p>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="flex items-center mb-3">
                  <FontAwesomeIcon icon={faStar} className="text-pink-500 mr-3" />
                  <h3 className="font-semibold text-gray-700">User-friendly Interface</h3>
                </div>
                <p className="text-gray-600">Clean, intuitive design with clear labels and easy-to-use input fields.</p>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="flex items-center mb-3">
                  <FontAwesomeIcon icon={faChartLine} className="text-red-500 mr-3" />
                  <h3 className="font-semibold text-gray-700">Data Visualization</h3>
                </div>
                <p className="text-gray-600">Interactive graph that updates automatically with your calculations.</p>
              </div>
            </div>
          </div>

          <div className="mt-8 bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4 text-blue-600">FAQ</h2>
            <div className="space-y-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-700 mb-2">What is markup percentage?</h3>
                <p className="text-gray-600">Markup percentage is the amount added to the cost price to determine the selling price. It's expressed as a percentage of the cost price.</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-700 mb-2">How is markup different from margin?</h3>
                <p className="text-gray-600">Markup is calculated based on cost price, while margin is calculated based on selling price. Markup is always higher than margin for the same dollar amount.</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-700 mb-2">Why is the graph important?</h3>
                <p className="text-gray-600">The graph provides a visual representation of your price progression, helping you understand the relationship between cost price, selling price, and profit margin.</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-700 mb-2">How do I calculate markup from selling price?</h3>
                <p className="text-gray-600">Enter your cost price and selling price in the "Calculate from Selling Price" section. The calculator will show you the markup percentage.</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-700 mb-2">What does the line slope represent?</h3>
                <p className="text-gray-600">The slope of the line in the graph represents your markup percentage. A steeper line indicates a higher markup percentage.</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-700 mb-2">How accurate are the calculations?</h3>
                <p className="text-gray-600">The calculator uses precise mathematical formulas to ensure accurate results. All calculations are rounded to two decimal places for readability.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarkupCalculatorClassic;
